import { DateRangePicker, Input, MultiSelect, Select, Text, type TypeSelectOption } from '@thefarmersfront/kitchen';

import clsx from 'clsx';
import dayjs from 'dayjs';

import { SearchFilter } from '~/components/common';

import { OPTION_ALL } from '~/constants';
import { COLLECTING_SHIPPING_LABEL_STATUS } from '~/constants/delivery-agency';

import type {
  CollectingResultReportFilterOptions,
  CollectingResultReportFilterValues,
  CollectingResultReportFilterValuesStatus,
} from '~/types/delivery-agency.types';

import styles from './CollectingResultReportFilter.module.scss';

const CollectingResultReportFilter = ({
  options,
  values,
  valuesStatus,
  onChange,
  onChangeStatus,
  onSearch,
  onReset,
}: {
  options: CollectingResultReportFilterOptions;
  values: CollectingResultReportFilterValues;
  valuesStatus: CollectingResultReportFilterValuesStatus;
  onChange: (values: Partial<CollectingResultReportFilterValues>) => void;
  onChangeStatus: (values: Partial<CollectingResultReportFilterValuesStatus>) => void;
  onSearch: () => void;
  onReset: () => void;
}) => {
  const hasFailedStatus = values.collectingShippingLabelStatuses.some(
    (status) =>
      status.value === OPTION_ALL.value || status.value === COLLECTING_SHIPPING_LABEL_STATUS.COLLECT_FAILED.value
  );

  /** 화면설계: 회수 상태에서 회수 실패를 빼면 실패 사유는 전체 선택 상태로 되돌린 뒤 비활성화된다 */
  const handleChangeStatuses = (statuses: TypeSelectOption<string>[]) => {
    const nextHasFailedStatus = statuses.some(
      (status) =>
        status.value === OPTION_ALL.value || status.value === COLLECTING_SHIPPING_LABEL_STATUS.COLLECT_FAILED.value
    );

    onChange({
      collectingShippingLabelStatuses: statuses,
      ...(nextHasFailedStatus ? {} : { collectingFailureReasonCategories: [OPTION_ALL] }),
    });
  };

  return (
    <SearchFilter formId="collecting-result-report-search-form" onSubmit={onSearch} onReset={onReset}>
      <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
        <Text size="small" weight="semi-bold" element="p">
          키워드
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.searchKeywordTypes}
            value={values.keywordType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.keywordType?.value) return;
              onChange({ keywordType: value, keyword: '' });
              onChangeStatus({ keyword: 'default' });
            }}
          />
          <Input
            type="text"
            size="small"
            placeholder="입력"
            maxLength={50}
            value={values.keyword}
            status={valuesStatus.keyword}
            onChange={(e) => onChange({ keyword: e.target.value })}
            onBlur={(e) => onChange({ keyword: e.target.value.trim() })}
            onFocus={() => onChangeStatus({ keyword: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-date'])}>
        <Text size="small" weight="semi-bold" element="p">
          반품접수일
        </Text>
        <div className={styles['input-container']}>
          <DateRangePicker
            size="small"
            placeholder={{ start: '시작일', end: '종료일' }}
            minDate={dayjs().subtract(3, 'month').startOf('month').toDate()}
            maxDate={dayjs().endOf('day').toDate()}
            value={values.collectingRequestedDateRange}
            status={valuesStatus.collectingRequestedDateRange}
            onChange={(value) => onChange({ collectingRequestedDateRange: value })}
            onFocus={() => onChangeStatus({ collectingRequestedDateRange: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-status'])}>
        <Text size="small" weight="semi-bold" element="p">
          회수 상태
        </Text>
        <div className={styles['input-container']}>
          <MultiSelect
            size="small"
            placeholder="선택 (복수 선택 가능)"
            options={[OPTION_ALL, ...options.collectingShippingLabelStatuses]}
            value={values.collectingShippingLabelStatuses}
            allOption={OPTION_ALL}
            onChange={(_, value) => handleChangeStatuses(value ?? [])}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-failure-reason'])}>
        <Text size="small" weight="semi-bold" element="p">
          회수 실패 사유
        </Text>
        <div className={styles['input-container']}>
          <MultiSelect
            size="small"
            placeholder="선택 (복수 선택 가능)"
            disabled={!hasFailedStatus}
            options={[OPTION_ALL, ...options.collectingFailureReasonCategories]}
            value={values.collectingFailureReasonCategories}
            allOption={OPTION_ALL}
            onChange={(_, value) => onChange({ collectingFailureReasonCategories: value ?? [] })}
          />
        </div>
      </div>
    </SearchFilter>
  );
};

export default CollectingResultReportFilter;
